import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import {
    FileText, CheckCircle2, XCircle, Clock, ShieldCheck,
    ExternalLink, Mail, User, Phone, Building,
    Stethoscope, Briefcase, AlertCircle, UploadCloud,
    MapPin, CreditCard, Calendar, Activity, FileCheck, Globe, Info, Heart,
    FileWarning, PlusCircle, RefreshCw, Trash2, Send, Search, Eye
} from 'lucide-react';

const REQUIRED_DOCS = {
    doctor: [
        { type: 'PHOTO', label: 'Profile Photo' },
        { type: 'AADHAAR_CARD', label: 'Aadhaar Card' },
        { type: 'PAN_CARD', label: 'PAN Card' },
        { type: 'MEDICAL_DEGREE', label: 'Medical Degree' },
        { type: 'REGISTRATION_CERTIFICATE', label: 'Council Registration' },
        { type: 'CANCELLED_CHEQUE', label: 'Bank Proof' }
    ],
    marketing_agent: [
        { type: 'PHOTO', label: 'Profile Photo' },
        { type: 'AADHAAR_CARD', label: 'Aadhaar Card' },
        { type: 'PAN_CARD', label: 'PAN Card' },
        { type: 'CANCELLED_CHEQUE', label: 'Bank Proof' }
    ],
    hospital: [
        { type: 'PHOTO', label: 'Institutional Photo' },
        { type: 'HOSPITAL_REGISTRATION', label: 'Hospital Registration' },
        { type: 'PAN_CARD', label: 'PAN Card' },
        { type: 'GST_CERTIFICATE', label: 'GST Certificate' },
        { type: 'CANCELLED_CHEQUE', label: 'Bank Proof' }
    ]
};

const CandidateAvatar = ({ candidate, sizeClasses, textClasses }) => {
    const [error, setError] = React.useState(false);

    // Reset error state when candidate changes (important for sync)
    React.useEffect(() => {
        setError(false);
    }, [candidate?._id, candidate?.onboardingDocuments]);

    const photo = candidate?.onboardingDocuments?.find(d => d.type === 'PHOTO');
    const displayName = (c) => {
        if (!c) return '—';
        if (c.role === 'hospital' && c.onboardingData?.hospitalName) return c.onboardingData.hospitalName;
        const fullName = `${c.firstName || ''} ${c.lastName || ''}`.trim();
        return fullName || c.email || 'Anonymous';
    };

    if (!photo || error) {
        return <span className={textClasses}>{displayName(candidate).charAt(0)}</span>;
    }

    return (
        <img
            src={photo.url}
            alt="Profile"
            className="w-full h-full object-cover"
            onError={() => setError(true)}
        />
    );
};

const OnboardingReview = () => {
    const { user } = useAuth();
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [processingAction, setProcessingAction] = useState(null); // 'verified' | 'rejected' | null
    const [searchQuery, setSearchQuery] = useState('');

    // Multi-select & Bulk Delete States
    const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    const [deletingBulk, setDeletingBulk] = useState(false);

    // Admin Override States (Reminders & Form Fill)
    const [editData, setEditData] = useState({});
    const [updating, setUpdating] = useState(false);
    const [missingNote, setMissingNote] = useState('');
    const [reminding, setReminding] = useState(false);
    const [generatedLink, setGeneratedLink] = useState('');
    const [iframeToken, setIframeToken] = useState(null);
    const [iframeRole, setIframeRole] = useState(null);

    const handleBulkDeleteCandidates = async () => {
        if (selectedCandidateIds.length === 0) return;
        setDeletingBulk(true);
        try {
            await api.post('/users/bulk-delete', { userIds: selectedCandidateIds });
            toast.success(`Successfully deleted ${selectedCandidateIds.length} application(s)!`);
            if (selectedCandidate && selectedCandidateIds.includes(selectedCandidate._id)) {
                setSelectedCandidate(null);
            }
            setSelectedCandidateIds([]);
            setShowBulkDeleteModal(false);
            fetchCandidates();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Bulk deletion failed');
        } finally {
            setDeletingBulk(false);
        }
    };

    useEffect(() => {
        fetchCandidates();
        // Auto-refresh every 30 seconds for real-time sync
        const interval = setInterval(() => fetchCandidates(true), 30000);
        return () => clearInterval(interval);
    }, [selectedCandidate?._id]); // Re-sync when selection changes

    const fetchCandidates = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const res = await api.get('/onboarding/pending');
            const fetchedData = res.data.data;
            setCandidates(fetchedData);

            // Sync selected candidate with latest data
            if (selectedCandidate) {
                const latest = fetchedData.find(c => c._id === selectedCandidate._id);
                if (latest) {
                    setSelectedCandidate(latest);
                }
            }
        } catch (err) {
            if (!silent) toast.error('Failed to load pending applications');
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const handleUpdateCandidate = async () => {
        if (!selectedCandidate) return;
        setUpdating(true);
        try {
            const dataToSave = { ...editData };
            if (dataToSave.specialization === 'Others' && dataToSave.otherSpecialization) {
                dataToSave.specialization = dataToSave.otherSpecialization;
            }

            await api.patch(`/onboarding/update/${selectedCandidate._id}`, {
                onboardingData: dataToSave
            });
            toast.success('Member information updated.');
            setSelectedCandidate(prev => ({
                ...prev,
                onboardingData: { ...prev.onboardingData, ...editData }
            }));
            fetchCandidates();
        } catch (err) {
            const errorMsg = err.response?.data?.message || 'Failed to update candidate information.';
            const details = err.response?.data?.details;

            if (details && Array.isArray(details)) {
                const dupError = details.find(d => d.includes('E11000') && d.includes('registrationNumber'));
                if (dupError) {
                    toast.error('Protocol Update Failed: This Registration Number is already assigned!', {
                        duration: 6000,
                        icon: '⚠️'
                    });
                } else {
                    toast.error(`Update Error: ${details.join(', ')}`);
                }
            } else {
                toast.error(errorMsg);
            }
        } finally {
            setUpdating(false);
        }
    };

    const handleVerify = async (userId, status) => {
        setProcessingAction(status);
        try {
            await api.post(`/onboarding/verify/${userId}`, {
                status,
                reason: 'Administrative action taken from Dashboard'
            });
            toast.success(status === 'verified' ? 'Partner activated & Credentials sent!' : 'Application Rejected');
            setSelectedCandidate(null);
            fetchCandidates();
        } catch (err) {
            const errorMsg = err.response?.data?.message || 'Verification failed';
            const details = err.response?.data?.details;

            if (details && Array.isArray(details)) {
                const dupError = details.find(d => d.includes('E11000') && d.includes('registrationNumber'));
                if (dupError) {
                    toast.error('Critical Error: Registration Number already exists for another member!', {
                        duration: 6000,
                        icon: '🚫'
                    });
                } else {
                    toast.error(`Required Fields Missing: ${details.join(', ')}`, { duration: 6000 });
                }
            } else {
                toast.error(errorMsg);
            }
        } finally {
            setProcessingAction(null);
        }
    };

    const handleSendReminder = async (customNote = null) => {
        const note = typeof customNote === 'string' ? customNote : (missingNote || 'Please complete your pending onboarding requirements to activate your account.');
        setReminding(true);
        setGeneratedLink('');
        try {
            const res = await api.post('/onboarding/remind-missing', {
                userId: selectedCandidate._id,
                missingItems: note
            });
            toast.success('Gap Remediation Link Dispatched.');
            setGeneratedLink(res.data.link);
            setMissingNote('');
        } catch (err) {
            toast.error('Transmission failed');
        } finally {
            setReminding(false);
        }
    };

    const handleDocumentUpload = async (e, forcedType = null) => {
        const file = e.target.files[0];
        if (!file) return;

        setUpdating(true);
        try {
            const formData = new FormData();
            formData.append('documents', file); // Required by S3 middleware
            // 1. Upload to S3 via public endpoint
            const uploadRes = await api.post('/onboarding/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const uploadedFile = uploadRes.data.data;

            // 2. Add to User profile via admin endpoint
            await api.post('/onboarding/add-document', {
                userId: selectedCandidate._id,
                documentName: file.name,
                documentUrl: uploadedFile.url,
                documentType: forcedType || 'ADMIN_UPLOAD'
            });

            toast.success(`${forcedType || 'Document'} injected successfully.`);
            fetchCandidates(true);
        } catch (err) {
            toast.error('Document transmission failed');
        } finally {
            setUpdating(false);
        }
    };

    const handleDeleteCandidate = async () => {
        if (!selectedCandidate) return;
        const confirmed = window.confirm(`DANGER ZONE: Permanently delete ${getDisplayName(selectedCandidate)}'s application? This action will remove ALL data from the database and cannot be undone.`);
        if (!confirmed) return;

        setUpdating(true);
        try {
            await api.delete(`/users/${selectedCandidate._id}`);
            toast.success('Record permanently deleted from database.');
            setSelectedCandidate(null);
            fetchCandidates();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Deletion failed');
        } finally {
            setUpdating(false);
        }
    };

    const openCandidate = (c) => {
        setSelectedCandidate(c);
        setGeneratedLink('');
        const data = c.onboardingData || {};
        const knownDegrees = ['MD', 'MS', 'MEM', 'DM', 'MCh', 'DNB', 'Diploma'];
        if (data.specialization && !knownDegrees.includes(data.specialization)) {
            setEditData({
                ...data,
                specialization: 'Others',
                otherSpecialization: data.specialization
            });
        } else {
            setEditData(data);
        }
    };

    const getDisplayName = (candidate) => {
        if (!candidate) return '—';
        if (candidate.role === 'hospital' && candidate.onboardingData?.hospitalName) {
            return candidate.onboardingData.hospitalName;
        }
        const fullName = `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim();
        if (!fullName || fullName.includes('To Be Filled')) {
            return candidate.email || 'Anonymous';
        }
        return fullName;
    };

    const renderDetailContent = () => {
        if (!selectedCandidate) return null;
        const data = selectedCandidate.onboardingData || {};
        const docs = selectedCandidate.onboardingDocuments || [];

        // Find profile picture from documents
        const profilePic = docs.find(d =>
            d.type?.toLowerCase().includes('photo') ||
            d.name?.toLowerCase().includes('photo') ||
            d.type?.toLowerCase().includes('profile') ||
            d.name?.toLowerCase().includes('profile')
        );

        return (
            <div className="flex flex-col h-full overflow-hidden">
                {/* Detail Header */}
                <div className="bg-white px-6 py-4 border-b border-gray-100 flex flex-wrap gap-4 items-center justify-between flex-shrink-0 z-10 shadow-sm relative">
                    <div className="flex items-center gap-4 lg:gap-6 min-w-[240px] flex-1">
                        <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-[#143E78] rounded-xl sm:rounded-2xl flex items-center justify-center text-white text-lg sm:text-xl lg:text-2xl font-black shadow-lg overflow-hidden border-2 border-white">
                            <CandidateAvatar
                                candidate={selectedCandidate}
                                textClasses=""
                            />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                                <span className="bg-blue-100 text-[#143E78] text-[8px] font-black px-2 py-0.5 rounded uppercase tracking-widest whitespace-nowrap">
                                    {selectedCandidate.role?.replace(/_/g, ' ')}
                                </span>
                                <span className={`text-[8px] font-black px-2 py-0.5 rounded uppercase tracking-widest whitespace-nowrap ${selectedCandidate.onboardingStatus === 'submitted' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                    {selectedCandidate.onboardingStatus}
                                </span>
                            </div>
                            <h2 className="text-base lg:text-xl xl:text-2xl font-black text-slate-800 leading-tight truncate">
                                {getDisplayName(selectedCandidate)}
                            </h2>
                        </div>
                    </div>

                    <div className="flex flex-row flex-nowrap gap-2 items-center w-full xl:w-auto flex-shrink-0 relative">
                        <button
                            onClick={() => handleVerify(selectedCandidate._id, 'verified')}
                            disabled={processingAction !== null || selectedCandidate.onboardingStatus !== 'submitted'}
                            className="flex-1 xl:flex-none px-2 sm:px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-black rounded-xl flex items-center justify-center gap-1.5 sm:gap-2 shadow-md shadow-emerald-500/20 transition-all disabled:opacity-40 group whitespace-nowrap"
                        >
                            {processingAction === 'verified' ? (
                                <div className="w-4 h-4 lg:w-5 lg:h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            ) : (
                                <CheckCircle2 className="w-4 h-4 lg:w-5 lg:h-5 group-hover:scale-110 transition-transform" />
                            )}
                            <span className="text-[9px] sm:text-[10px] lg:text-[12px] uppercase tracking-tighter sm:tracking-normal">
                                {processingAction === 'verified' ? 'Processing...' : 'Approve & Activate'}
                            </span>
                        </button>
                        <button
                            onClick={() => handleVerify(selectedCandidate._id, 'rejected')}
                            disabled={processingAction !== null || (selectedCandidate.onboardingStatus !== 'submitted' && selectedCandidate.onboardingStatus !== 'pending')}
                            className="flex-1 xl:flex-none px-2 sm:px-4 py-2.5 bg-rose-500 hover:bg-rose-600 text-white font-black rounded-xl flex items-center justify-center gap-1.5 sm:gap-2 shadow-md shadow-rose-500/20 transition-all disabled:opacity-40 group whitespace-nowrap"
                        >
                            {processingAction === 'rejected' ? (
                                <div className="w-4 h-4 lg:w-5 lg:h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            ) : (
                                <XCircle className="w-4 h-4 lg:w-5 lg:h-5 group-hover:scale-110 transition-transform" />
                            )}
                            <span className="text-[9px] sm:text-[10px] lg:text-[12px] uppercase tracking-tighter sm:tracking-normal">
                                {processingAction === 'rejected' ? 'Processing...' : 'Reject Application'}
                            </span>
                        </button>

                        <div className="h-10 w-px bg-gray-100 mx-1 hidden sm:block"></div>

                        <button
                            onClick={() => handleSendReminder()}
                            disabled={reminding}
                            className="p-2.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-xl transition-all group relative"
                            title="Resend Onboarding Link"
                        >
                            {reminding ? (
                                <div className="w-4 h-4 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin"></div>
                            ) : (
                                <Send className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            )}
                        </button>

                        <button
                            onClick={() => {
                                setIframeToken(selectedCandidate.onboardingToken);
                                setIframeRole(selectedCandidate.role);
                            }}
                            disabled={!selectedCandidate.onboardingToken}
                            className="p-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-xl transition-all group relative disabled:opacity-50"
                            title="Fill Form on Behalf"
                        >
                            <FileText className="w-4 h-4 group-hover:scale-110 transition-transform" />
                        </button>

                        {user?.role === 'admin' && (
                            <button
                                onClick={handleDeleteCandidate}
                                disabled={updating}
                                className="p-2.5 bg-rose-50 hover:bg-rose-100 text-rose-400 hover:text-rose-600 rounded-xl transition-all"
                                title="Delete Application"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>

                <div className="flex-1 p-5 lg:p-10 space-y-10 overflow-y-auto custom-scrollbar bg-slate-50/30">

                    {/* SECTION 1: CORE CONTACT PROFILE */}
                    <div className="space-y-6">
                        <h3 className="text-[10px] font-black uppercase text-[#143E78] tracking-[0.3em] flex items-center gap-2">
                            <User className="w-4 h-4 text-blue-500" /> Core Contact Profile
                        </h3>
                        <div className="bg-white p-8 rounded-[40px] border border-gray-100 shadow-sm grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                            <div className="space-y-1">
                                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Email Address</p>
                                <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800 break-all leading-tight">{data.email || selectedCandidate.email}</p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Primary Phone</p>
                                <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800 break-all leading-tight">{data.phone || selectedCandidate.phone}</p>
                            </div>

                            {selectedCandidate.role === 'hospital' ? (
                                <>
                                    <div className="space-y-1">
                                        <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Alternative Emails</p>
                                        <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800 break-words leading-tight">{data.alternativeEmails?.filter(Boolean).join(', ') || 'Not Provided'}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Current City</p>
                                        <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800 break-words leading-tight">{data.city || data.hospitalCity || '—'}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Current Address</p>
                                        <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800 break-words leading-tight">{data.currentAddress || data.hospitalAddress || '—'}</p>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="space-y-1">
                                        <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Alt Phone / WhatsApp</p>
                                        <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800 break-words leading-tight">{data.alternativePhone || 'Not Provided'}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Gender & DOB</p>
                                        <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800 break-words leading-tight">{data.gender || '—'} / {data.dob || '—'}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Current City</p>
                                        <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800 break-words leading-tight">{data.city || '—'}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Emergency Contact</p>
                                        <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800 break-words leading-tight">{data.emergencyContact || 'Not Provided'}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Current Address</p>
                                        <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800 break-words leading-tight">{data.currentAddress || '—'}</p>
                                    </div>
                                </>
                            )}

                            {selectedCandidate.role === 'marketing_agent' && (
                                <div className="space-y-1">
                                    <p className="text-[8px] font-black text-emerald-400 uppercase tracking-widest">Assigned Region</p>
                                    <p className="text-[10px] sm:text-xs xl:text-sm font-black text-emerald-700 break-words leading-tight">{data.region || 'PENDING'}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {selectedCandidate.role === 'hospital' && data.hasDesignatedEmployee && (
                        <div className="space-y-6">
                            <h3 className="text-[10px] font-black uppercase text-[#143E78] tracking-[0.3em] flex items-center gap-2">
                                <User className="w-4 h-4 text-emerald-500" /> Designated Employee
                            </h3>
                            <div className="bg-emerald-50/30 p-8 rounded-[40px] border border-emerald-100 shadow-sm grid grid-cols-1 md:grid-cols-3 gap-8">
                                <div className="space-y-1">
                                    <p className="text-[8px] font-black text-emerald-600 uppercase tracking-widest">Employee Name</p>
                                    <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800 break-all leading-tight">{data.designatedEmployee?.name || '—'}</p>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-[8px] font-black text-emerald-600 uppercase tracking-widest">Job Role</p>
                                    <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800 break-all leading-tight">{data.designatedEmployee?.role || '—'}</p>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-[8px] font-black text-emerald-600 uppercase tracking-widest">WhatsApp Number</p>
                                    <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800 break-all leading-tight">{data.designatedEmployee?.phone || '—'}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* SECTION 2: ROLE SPECIFIC DEEP DIVE */}
                    {selectedCandidate.role === 'doctor' && (
                        <div className="space-y-10">
                            {/* Clinical Qualifications */}
                            <div className="space-y-6">
                                <h3 className="text-[10px] font-black uppercase text-[#143E78] tracking-[0.3em] flex items-center gap-2">
                                    <Stethoscope className="w-4 h-4 text-blue-500" /> Clinical Qualifications & Expertise
                                </h3>
                                <div className="bg-white p-8 rounded-[40px] border border-gray-100 shadow-sm grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="space-y-4">
                                        <div>
                                            <p className="text-[8px] font-black text-blue-400 uppercase mb-1">Primary Degree</p>
                                            <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800 italic">{data.primaryQualification} from {data.universityName} ({data.yearOfCompletion})</p>
                                        </div>
                                        <div>
                                            <p className="text-[8px] font-black text-blue-400 uppercase mb-1">Post Graduation / Specialization</p>
                                            <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800 italic">{data.specialization} - {data.specializationBranch} ({data.specializationYear || '—'})</p>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <p className="text-[8px] font-black text-emerald-400 uppercase mb-1">Professional Registration</p>
                                            <p className="text-[10px] sm:text-xs xl:text-sm font-black text-emerald-700">{data.registrationNumber} ({data.councilName})</p>
                                            <p className="text-[9px] sm:text-[10px] font-bold text-slate-400 mt-1">Validity: {data.registrationValidity || '—'}</p>
                                        </div>
                                        <div className="flex gap-4">
                                            <div className="bg-slate-50 p-4 rounded-2xl flex-1 text-center border border-slate-100">
                                                <p className="text-[8px] font-black text-slate-400 uppercase">Total Experience</p>
                                                <p className="text-xl font-black text-slate-800">{data.totalExperience}Y</p>
                                            </div>
                                            <div className="bg-slate-50 p-4 rounded-2xl flex-1 text-center border border-slate-100">
                                                <p className="text-[8px] font-black text-slate-400 uppercase">Post-Spec Exp</p>
                                                <p className="text-xl font-black text-slate-800">{data.postSpecializationExperience || '0'}Y</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>


                        </div>
                    )}

                    {selectedCandidate.role === 'marketing_agent' && (
                        <div className="space-y-10">
                            {/* Professional Profile */}
                            <div className="space-y-6">
                                <h3 className="text-[10px] font-black uppercase text-[#143E78] tracking-[0.3em] flex items-center gap-2">
                                    <Briefcase className="w-4 h-4 text-emerald-500" /> Lead Partner Profile
                                </h3>
                                <div className="bg-white p-8 rounded-[40px] border border-gray-100 shadow-sm grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                                    <div className="flex flex-col items-center gap-4 p-8 bg-slate-50/50 rounded-[32px] border border-slate-100">
                                        <div className="w-32 h-32 bg-[#143E78] rounded-[40px] shadow-2xl flex items-center justify-center text-white text-4xl font-black overflow-hidden border-4 border-white">
                                            <CandidateAvatar candidate={selectedCandidate} textClasses="" />
                                        </div>
                                        <div className="text-center">
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Onboarding Photo</p>
                                            <span className="bg-emerald-100 text-emerald-700 text-[8px] font-black px-3 py-1 rounded-full uppercase italic tracking-widest">Verified Identity</span>
                                        </div>
                                    </div>
                                    <div className="space-y-8">
                                        <div className="grid grid-cols-2 gap-6">
                                            <div className="space-y-1">
                                                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Assigned Region</p>
                                                <p className="text-[10px] sm:text-xs xl:text-sm font-black text-[#143E78] italic uppercase tracking-tight">{data.region || 'Pending Assignment'}</p>
                                            </div>
                                            <div className="space-y-1">
                                                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Experience</p>
                                                <p className="text-[10px] sm:text-xs xl:text-sm font-black text-[#143E78]">{data.totalExperience || '0'} Years</p>
                                            </div>
                                        </div>
                                        <div className="pt-6 border-t border-slate-100">
                                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-2">Professional Expertise</p>
                                            <p className="text-xs font-bold text-slate-600 leading-relaxed italic">"{data.optionalExpertise || 'No additional expertise notes provided.'}"</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {selectedCandidate.role === 'hospital' && (
                        <div className="space-y-10">
                            {/* Entity & Governance */}
                            <div className="space-y-6">
                                <h3 className="text-[10px] font-black uppercase text-[#143E78] tracking-[0.3em] flex items-center gap-2">
                                    <Building className="w-4 h-4 text-blue-500" /> Institutional Governance
                                </h3>
                                <div className="bg-white p-8 rounded-[40px] border border-gray-100 shadow-sm grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                    <div className="space-y-1">
                                        <p className="text-[8px] font-black text-slate-400 uppercase">Hospital Name</p>
                                        <p className="text-xs sm:text-sm xl:text-lg font-black text-slate-800 italic">"{data.hospitalName}"</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-[8px] font-black text-slate-400 uppercase">Entity Type</p>
                                        <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800">{data.hospitalType}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-[8px] font-black text-slate-400 uppercase">Primary Admin / Contact</p>
                                        <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800">{data.entityContactName}</p>
                                        {data.entityContactEmail && <p className="text-[9px] font-black text-slate-600">{data.entityContactEmail}</p>}
                                        {data.entityContactPhone && <p className="text-[9px] font-black text-slate-600">{data.entityContactPhone}</p>}
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-[8px] font-black text-slate-400 uppercase">Registration No.</p>
                                        <p className="text-[10px] sm:text-xs xl:text-sm font-black text-blue-600 font-mono tracking-widest">{data.registrationNumber}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-[8px] font-black text-slate-400 uppercase">Issuing Authority</p>
                                        <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800">{data.issuingAuthority}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-[8px] font-black text-slate-400 uppercase">License Validity</p>
                                        <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800">{data.licenseValidity || '—'}</p>
                                    </div>
                                </div>
                                {(data.hospitalPartners?.length > 0 || data.authorizedSignatories?.length > 0) && (
                                    <div className="bg-white p-8 rounded-[40px] border border-gray-100 shadow-sm space-y-8">
                                        {data.hospitalPartners?.length > 0 && (
                                            <div>
                                                <h4 className="text-[9px] font-black uppercase text-[#143E78] tracking-widest mb-4 border-b pb-2">Partners / Directors</h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {data.hospitalPartners.map((partner, idx) => (
                                                        <div key={idx} className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                                                            <p className="text-xs font-black text-slate-800">{partner.name || `Partner ${idx + 1}`}</p>
                                                            {partner.email && <p className="text-[10px] font-bold text-slate-500 mt-1">{partner.email}</p>}
                                                            {partner.phone && <p className="text-[10px] font-bold text-slate-500">{partner.phone}</p>}
                                                            {partner.signatureUrl && (
                                                                <div className="mt-3 pt-3 border-t border-slate-200/50">
                                                                    <p className="text-[8px] font-black uppercase text-emerald-600 mb-1">Signature Attached</p>
                                                                    <a href={partner.signatureUrl} target="_blank" rel="noreferrer" className="text-[9px] font-bold text-blue-600 hover:underline flex items-center gap-1">
                                                                        <Eye className="w-3 h-3" /> View Document
                                                                    </a>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {data.authorizedSignatories?.length > 0 && (
                                            <div>
                                                <h4 className="text-[9px] font-black uppercase text-[#143E78] tracking-widest mb-4 border-b pb-2">Authorized Signatories</h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {data.authorizedSignatories.map((sig, idx) => (
                                                        <div key={idx} className="bg-blue-50/50 p-4 rounded-2xl border border-blue-100/50">
                                                            <p className="text-xs font-black text-slate-800 flex items-center gap-2">
                                                                {sig.name || `Signatory ${idx + 1}`}
                                                                {data.authorizedSignatoryIndex == idx && (
                                                                    <span className="text-[8px] bg-[#143E78] text-white px-2 py-0.5 rounded-full">MoU SELECT</span>
                                                                )}
                                                            </p>
                                                            {sig.email && <p className="text-[10px] font-bold text-slate-500 mt-1">{sig.email}</p>}
                                                            {sig.phone && <p className="text-[10px] font-bold text-slate-500">{sig.phone}</p>}
                                                            {sig.signatureUrl && (
                                                                <div className="mt-3 pt-3 border-t border-blue-200/50">
                                                                    <p className="text-[8px] font-black uppercase text-emerald-600 mb-1">Signature Attached</p>
                                                                    <a href={sig.signatureUrl} target="_blank" rel="noreferrer" className="text-[9px] font-bold text-blue-600 hover:underline flex items-center gap-1">
                                                                        <Eye className="w-3 h-3" /> View Document
                                                                    </a>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Infrastructure & Capability */}
                            <div className="space-y-6">
                                <h3 className="text-[10px] font-black uppercase text-[#143E78] tracking-[0.3em] flex items-center gap-2">
                                    <Activity className="w-4 h-4 text-blue-500" /> Infrastructure & Capability
                                </h3>
                                <div className="bg-white p-8 rounded-[40px] border border-gray-100 shadow-sm grid grid-cols-1 lg:grid-cols-2 gap-10">
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="text-center bg-blue-50 p-6 rounded-3xl border border-blue-100 shadow-inner">
                                            <p className="text-[8px] font-black text-blue-400 uppercase">Total Beds</p>
                                            <p className="text-xl sm:text-2xl xl:text-3xl font-black text-[#143E78]">{data.totalBeds}</p>
                                        </div>
                                        <div className="text-center bg-blue-50 p-6 rounded-3xl border border-blue-100 shadow-inner">
                                            <p className="text-[8px] font-black text-blue-400 uppercase">ICU Beds</p>
                                            <p className="text-xl sm:text-2xl xl:text-3xl font-black text-[#143E78]">{data.icuBeds}</p>
                                        </div>
                                        <div className="text-center bg-blue-50 p-6 rounded-3xl border border-blue-100 shadow-inner">
                                            <p className="text-[8px] font-black text-blue-400 uppercase">OT Rooms</p>
                                            <p className="text-xl sm:text-2xl xl:text-3xl font-black text-[#143E78]">{data.otRooms}</p>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                            <span className="text-[10px] font-black uppercase tracking-widest">NABH Accreditation</span>
                                            {data.nabhAccredited ? (
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[10px] font-black text-emerald-600">{data.nabhNumber}</span>
                                                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                                                </div>
                                            ) : <XCircle className="w-5 h-5 text-gray-300" />}
                                        </div>
                                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                            <span className="text-[10px] font-black uppercase tracking-widest">Ayushman Bharat</span>
                                            {data.ayushmanBharatEmpanelled ? (
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[10px] font-black text-emerald-600">{data.ayushmanNumber}</span>
                                                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                                                </div>
                                            ) : <XCircle className="w-5 h-5 text-gray-300" />}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Digital Presence */}
                            <div className="space-y-6">
                                <h3 className="text-[10px] font-black uppercase text-[#143E78] tracking-[0.3em] flex items-center gap-2">
                                    <Globe className="w-4 h-4 text-emerald-500" /> Digital Footprint
                                </h3>
                                <div className="bg-white p-8 rounded-[40px] border border-gray-100 shadow-sm grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div>
                                        <p className="text-[8px] font-black text-slate-400 uppercase mb-2">Google Maps Geolocation</p>
                                        <a href={data.googleMapLocation} target="_blank" rel="noreferrer" className="text-xs font-black text-blue-600 flex items-center gap-2 hover:underline">
                                            <MapPin className="w-4 h-4" /> OPEN LIVE COORDINATES
                                        </a>
                                    </div>
                                    <div>
                                        <p className="text-[8px] font-black text-slate-400 uppercase mb-2">Institutional Website</p>
                                        <a href={data.websiteUrl} target="_blank" rel="noreferrer" className="text-xs font-black text-blue-600 flex items-center gap-2 hover:underline">
                                            <ExternalLink className="w-4 h-4" /> VISIT OFFICIAL DOMAIN
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* SECTION 3: FINANCIAL & KYC (COMMON) */}
                    <div className="space-y-6">
                        <h3 className="text-[10px] font-black uppercase text-[#143E78] tracking-[0.3em] flex items-center gap-2">
                            <CreditCard className="w-4 h-4 text-blue-500" /> Financial & KYC Assets
                        </h3>
                        <div className="bg-white p-8 rounded-[40px] border border-gray-100 shadow-sm space-y-10">
                            {/* Bank Account */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                                <div className="space-y-1">
                                    <p className="text-[8px] font-black text-slate-400 uppercase">Account Holder</p>
                                    <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800 italic">{data.bankDetails?.accountName || '—'}</p>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-[8px] font-black text-slate-400 uppercase">Account Number</p>
                                    <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800 font-mono tracking-widest">{data.bankDetails?.accountNumber || '—'}</p>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-[8px] font-black text-slate-400 uppercase">Bank Name</p>
                                    <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800">{data.bankDetails?.bankName || '—'}</p>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-[8px] font-black text-slate-400 uppercase">IFSC Code</p>
                                    <p className="text-[10px] sm:text-xs xl:text-sm font-black text-emerald-600 font-mono tracking-widest">{data.bankDetails?.ifscCode || '—'}</p>
                                </div>
                            </div>
                            {/* Tax & IDs */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 pt-8 border-t border-gray-50">
                                <div className="space-y-1">
                                    <p className="text-[8px] font-black text-slate-400 uppercase">PAN Number</p>
                                    <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800 font-mono tracking-widest uppercase">{data.panNumber || '—'}</p>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-[8px] font-black text-slate-400 uppercase">Aadhaar UID</p>
                                    <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800 font-mono tracking-widest">{data.aadhaarNumber || '—'}</p>
                                </div>
                                {selectedCandidate.role === 'hospital' && (
                                    <>
                                        <div className="space-y-1">
                                            <p className="text-[8px] font-black text-slate-400 uppercase">GSTIN Number</p>
                                            <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800 font-mono tracking-widest uppercase">{data.gstNumber || '—'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-[8px] font-black text-slate-400 uppercase">GST Billing Name</p>
                                            <p className="text-[10px] sm:text-xs xl:text-sm font-black text-slate-800 italic uppercase">{data.gstBillingName || '—'}</p>
                                        </div>
                                    </>
                                )}
                            </div>
                            {/* Insurance for Doctors */}
                            {selectedCandidate.role === 'doctor' && (
                                <div className="pt-8 border-t border-gray-50">
                                    <p className="text-[8px] font-black text-slate-400 uppercase mb-4">Professional Indemnity Insurance</p>
                                    {data.insurance?.hasInsurance ? (
                                        <div className="bg-emerald-50/50 p-6 rounded-3xl border border-emerald-100 grid grid-cols-1 md:grid-cols-3 gap-6">
                                            <div>
                                                <p className="text-[8px] font-black text-emerald-400 uppercase">Provider</p>
                                                <p className="text-[10px] sm:text-xs xl:text-sm font-black text-emerald-800 italic uppercase">{data.insurance.provider}</p>
                                            </div>
                                            <div>
                                                <p className="text-[8px] font-black text-emerald-400 uppercase">Policy No.</p>
                                                <p className="text-[10px] sm:text-xs xl:text-sm font-black text-emerald-800 font-mono tracking-tighter uppercase">{data.insurance.policyNumber}</p>
                                            </div>
                                            <div>
                                                <p className="text-[8px] font-black text-emerald-400 uppercase">Valid Till</p>
                                                <p className="text-[10px] sm:text-xs xl:text-sm font-black text-emerald-800 italic uppercase">{data.insurance.validTill}</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="bg-amber-50/50 p-6 rounded-3xl border border-amber-100 flex items-center gap-3">
                                            <AlertCircle className="w-5 h-5 text-amber-500" />
                                            <p className="text-xs font-black text-amber-700 italic uppercase tracking-widest">Self-Insured / No Policy Provided</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* SECTION 3.5: COMPLIANCE ASSET CHECKLIST */}
                    <div className="space-y-8 pt-10">
                        <h3 className="text-[11px] font-black uppercase text-amber-600 tracking-[0.3em] flex items-center gap-2 px-2">
                            <ShieldCheck className="w-5 h-5 text-amber-500" /> Compliance Asset Checklist
                        </h3>
                        <div className="bg-white border border-amber-100 rounded-[40px] p-6 md:p-8 shadow-sm">
                            <div className="grid grid-cols-1 2xl:grid-cols-2 gap-6">
                                {(() => {
                                    const requirements = REQUIRED_DOCS[selectedCandidate.role] || [];
                                    const uploadedDocs = docs || [];

                                    return requirements.map((req, idx) => {
                                        const isPresent = uploadedDocs.some(d => d.type === req.type);
                                        return (
                                            <div key={idx} className={`flex flex-col sm:flex-row items-center justify-center sm:justify-between p-6 sm:p-5 rounded-[32px] border transition-all gap-5 sm:gap-4 ${isPresent ? 'bg-emerald-50/20 border-emerald-100/50 shadow-sm shadow-emerald-500/5' : 'bg-amber-50/20 border-amber-100/50 shadow-sm shadow-amber-500/5'}`}>
                                                <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 min-w-0 flex-1 w-full sm:w-auto">
                                                    {/* Icon Container */}
                                                    <div className={`w-12 h-12 rounded-2xl flex-shrink-0 flex items-center justify-center ${isPresent ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
                                                        {isPresent ? <CheckCircle2 className="w-6 h-6" /> : <AlertCircle className="w-6 h-6" />}
                                                    </div>

                                                    {/* Primary Info Segment */}
                                                    <div className="min-w-0 sm:min-w-[140px] flex-1 text-center sm:text-left">
                                                        <p className={`text-xs font-black uppercase tracking-tight leading-tight ${isPresent ? 'text-emerald-900' : 'text-amber-900'}`}>{req.label}</p>
                                                        <p className={`text-[9px] font-bold uppercase tracking-widest mt-1 ${isPresent ? 'text-emerald-500' : 'text-amber-500'}`}>
                                                            {isPresent ? 'Verified Asset' : 'Pending Upload'}
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Interaction Zone */}
                                                <div className="flex-shrink-0 w-full sm:w-auto flex justify-center sm:justify-end">
                                                    <input
                                                        type="file"
                                                        id={`upload-${req.type}`}
                                                        className="hidden"
                                                        onChange={(e) => handleDocumentUpload(e, req.type)}
                                                    />
                                                    <label
                                                        htmlFor={`upload-${req.type}`}
                                                        className={`flex items-center justify-center gap-2 px-1 sm:px-5 py-3 rounded-2xl cursor-pointer transition-all shadow-sm active:scale-95 text-[10px] font-black uppercase tracking-widest ${isPresent ? 'bg-white border border-slate-200 text-slate-500 hover:bg-slate-50' : 'bg-[#143E78] text-white hover:bg-blue-800 shadow-blue-900/20'}`}
                                                    >
                                                        {isPresent ? <RefreshCw className="w-4 h-4" /> : <PlusCircle className="w-4 h-4" />}
                                                        <span>{isPresent ? 'Replace' : 'Inject'}</span>
                                                    </label>
                                                </div>
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        </div>
                    </div>

                    {/* SECTION 4: INSTITUTIONAL DOCUMENT VAULT (ALL DOCUMENTS) */}
                    <div className="space-y-6 pt-10">
                        <h3 className="text-[10px] font-black uppercase text-[#143E78] tracking-[0.3em] flex items-center gap-2">
                            <FileText className="w-4 h-4 text-blue-500" /> Member Document Vault
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                            {docs.length === 0 ? (
                                <div className="col-span-full py-12 bg-white rounded-[40px] border border-dashed border-gray-200 text-center flex flex-col items-center gap-3">
                                    <UploadCloud className="w-10 h-10 text-gray-200" />
                                    <p className="text-xs font-black text-gray-400 uppercase italic">No digital assets available for this profile</p>
                                </div>

                            ) : (
                                docs.map((doc, idx) => (
                                    <a key={idx} href={doc.url} target="_blank" rel="noreferrer" className="p-6 bg-white border border-gray-100 rounded-[32px] hover:border-blue-400 hover:shadow-2xl hover:-translate-y-1 transition-all flex flex-col group relative overflow-hidden">
                                        <div className="absolute top-0 right-0 w-20 h-20 bg-blue-50 rounded-full blur-[40px] -mr-10 -mt-10 group-hover:bg-blue-200/40 transition-colors"></div>
                                        <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"><FileText className="w-6 h-6 text-[#143E78]" /></div>
                                        <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-1 italic">
                                            {doc.type?.replace(/_/g, ' ') || 'Supporting File'}
                                        </span>
                                        <p className="text-xs font-black text-slate-700 truncate mb-4 italic leading-relaxed">"{doc.name}"</p>
                                        <div className="mt-auto flex items-center justify-between">
                                            <div className="flex items-center text-blue-600 font-black text-[10px] gap-1 group-hover:gap-2 transition-all">
                                                VIEW <ExternalLink className="w-3 h-3" />
                                            </div>
                                            <span className="bg-emerald-50 text-emerald-600 text-[8px] font-black px-2 py-0.5 rounded uppercase">Encrypted</span>
                                        </div>
                                    </a>
                                ))
                            )}
                        </div>
                    </div>

                    {/* SECTION 5.5: ADMIN CORRECTIONS (STASHED EDITING LOGIC) */}
                    <div className="mt-12 space-y-10 bg-slate-50/50 p-8 rounded-[40px] border border-slate-100">
                        <div className="space-y-10">
                            {/* Segment: Clinical Credentials */}
                            {selectedCandidate.role === 'doctor' && (
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <p className="text-[10px] font-black text-amber-600/50 uppercase tracking-[0.3em] flex items-center gap-2">
                                            <span className="w-8 h-px bg-amber-200 inline-block"></span> Clinical Credentials
                                        </p>
                                        <div className="relative">
                                            <input
                                                type="file"
                                                id="adminDocUpload"
                                                className="hidden"
                                                onChange={handleDocumentUpload}
                                                accept=".pdf,.jpg,.jpeg,.png"
                                            />
                                            <label
                                                htmlFor="adminDocUpload"
                                                className="flex items-center gap-2 px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-700 text-[9px] font-black uppercase tracking-widest rounded-xl cursor-pointer transition-all shadow-sm"
                                            >
                                                <UploadCloud className="w-3.5 h-3.5" /> Inject Degree/Reg Doc
                                            </label>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-amber-700 uppercase pl-1">Gender</label>
                                            <select
                                                className="w-full bg-white border border-amber-200 rounded-2xl px-5 py-4 text-xs font-bold text-[#143E78] outline-none h-14"
                                                value={editData.gender || ''}
                                                onChange={(e) => setEditData({ ...editData, gender: e.target.value })}
                                            >
                                                <option value="">Select Gender</option>
                                                <option value="Male">Male</option>
                                                <option value="Female">Female</option>
                                                <option value="Other">Other</option>
                                                <option value="Prefer Not to Say">Prefer Not to Say</option>
                                            </select>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-amber-700 uppercase pl-1">Date of Birth</label>
                                            <input
                                                type="date"
                                                className="w-full bg-white border border-amber-200 rounded-2xl px-5 py-4 text-xs font-bold text-[#143E78] outline-none h-14"
                                                value={editData.dob ? editData.dob.substring(0, 10) : ''}
                                                onChange={(e) => setEditData({ ...editData, dob: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-amber-700 uppercase pl-1">Primary Degree</label>
                                            <select
                                                className="w-full bg-white border border-amber-200 rounded-2xl px-5 py-4 text-xs font-bold text-[#143E78] outline-none h-14"
                                                value={editData.primaryQualification || ''}
                                                onChange={(e) => setEditData({ ...editData, primaryQualification: e.target.value })}
                                            >
                                                {['MBBS', 'BDS', 'BAMS', 'BHMS'].map(q => <option key={q} value={q}>{q}</option>)}
                                            </select>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-amber-700 uppercase pl-1">Specialization Type</label>
                                            <select
                                                className="w-full bg-white border border-amber-200 rounded-2xl px-5 py-4 text-xs font-bold text-[#143E78] outline-none h-14"
                                                value={editData.specialization || ''}
                                                onChange={(e) => setEditData({ ...editData, specialization: e.target.value })}
                                            >
                                                <option value="">Select Degree</option>
                                                {['MD', 'MS', 'MEM', 'DM', 'MCh', 'DNB', 'Diploma', 'Others'].map(d => (
                                                    <option key={d} value={d}>{d}</option>
                                                ))}
                                            </select>
                                        </div>
                                        {editData.specialization === 'Others' && (
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-amber-700 uppercase pl-1">Specify Degree</label>
                                                <input
                                                    className="w-full bg-white border border-amber-200 rounded-2xl px-5 py-4 text-xs font-bold text-[#143E78] outline-none h-14"
                                                    placeholder="e.g. PhD"
                                                    value={editData.otherSpecialization || ''}
                                                    onChange={(e) => setEditData({ ...editData, otherSpecialization: e.target.value })}
                                                />
                                            </div>
                                        )}
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-amber-700 uppercase pl-1">Specialization Branch</label>
                                            <input
                                                className="w-full bg-white border border-amber-200 rounded-2xl px-5 py-4 text-xs font-bold text-[#143E78] outline-none h-14"
                                                placeholder="e.g. Cardiology"
                                                value={editData.specializationBranch || ''}
                                                onChange={(e) => setEditData({ ...editData, specializationBranch: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-amber-700 uppercase pl-1">Reg Number (KMC/PMC)</label>
                                            <input
                                                className="w-full bg-white border border-amber-200 rounded-2xl px-5 py-4 text-xs font-bold text-[#143E78] focus:ring-2 focus:ring-amber-400 outline-none h-14"
                                                placeholder="State License #"
                                                value={editData.registrationNumber || ''}
                                                onChange={(e) => setEditData({ ...editData, registrationNumber: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="pt-6 border-t border-slate-200">
                                <button
                                    onClick={handleUpdateCandidate}
                                    disabled={updating}
                                    className="w-full bg-[#143E78] hover:bg-blue-900 text-white py-5 rounded-[28px] text-[11px] font-black uppercase tracking-[0.2em] shadow-xl shadow-blue-900/20 transition-all disabled:opacity-50 flex items-center justify-center gap-3"
                                >
                                    {updating && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>}
                                    {updating ? 'Securing Data...' : 'Commit Protocol Update'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* SECTION 6: GAP REMEDIATION */}
                    <div className="mt-8">
                        {/* Gap Remediation Hub */}
                        <div className="bg-[#FFF1F2] p-10 rounded-[48px] border border-rose-200 shadow-xl shadow-rose-900/5 relative overflow-hidden">
                            <div className="flex items-center gap-3 mb-8">
                                <div className="w-10 h-10 bg-rose-500 rounded-xl flex items-center justify-center text-white"><AlertCircle className="w-5 h-5" /></div>
                                <div>
                                    <h4 className="text-lg font-black uppercase text-rose-800 italic leading-none">Gap Remediation Hub</h4>
                                    <p className="text-[8px] font-black text-rose-600 uppercase tracking-widest mt-1">Direct Dispute Resolution Protocol</p>
                                </div>
                            </div>
                            <textarea className="w-full bg-white border border-rose-200 rounded-[32px] p-6 text-xs font-bold h-32 focus:ring-4 focus:ring-rose-100 outline-none transition-all placeholder:text-gray-300" placeholder="Specify missing assets or data gaps..." value={missingNote} onChange={(e) => setMissingNote(e.target.value)} />
                            <button disabled={reminding || !missingNote} onClick={handleSendReminder} className="w-full bg-rose-600 hover:bg-rose-700 text-white py-5 rounded-[28px] text-[11px] font-black uppercase tracking-[0.2em] mt-6 shadow-xl shadow-rose-600/20 disabled:opacity-50 transition-all active:scale-95">
                                {reminding ? 'Transmitting Gap Log...' : 'Dispatch Remediation Link'}
                            </button>

                            {generatedLink && (
                                <div className="mt-6 p-6 bg-white/60 rounded-[32px] border border-rose-100 animate-in zoom-in-95 duration-500">
                                    <p className="text-[9px] font-black text-rose-400 uppercase tracking-widest mb-3">Live Remediation Link</p>
                                    <div className="flex items-center gap-2">
                                        <input
                                            readOnly
                                            value={generatedLink}
                                            className="flex-1 bg-white border border-rose-100 rounded-xl px-4 py-3 text-[10px] font-bold text-rose-800 outline-none"
                                            onClick={(e) => e.target.select()}
                                        />
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(generatedLink);
                                                toast.success('Link copied to clipboard');
                                            }}
                                            className="p-3 bg-rose-100 text-rose-600 rounded-xl hover:bg-rose-200 transition-colors"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <p className="text-[8px] font-bold text-rose-400 mt-3 italic text-center">Admin can manually share this link if email fails.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer Info */}
                    <div className="pt-12 flex flex-col items-center gap-4 text-center">
                        <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center"><ShieldCheck className="w-6 h-6 text-blue-500" /></div>
                        <div>
                            <p className="text-[10px] font-black text-slate-800 uppercase tracking-[0.3em]">Compliance Checkpoint 2.1</p>
                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">End-to-End Encrypted Verification Session</p>
                        </div>
                    </div>

                </div>
            </div>
        );
    };

    const filteredCandidates = candidates.filter(candidate => {
        const nameToSearch = getDisplayName(candidate);
        const roleName = candidate.role?.replace(/_/g, ' ') || '';
        return (
            nameToSearch.toLowerCase().includes(searchQuery.toLowerCase()) ||
            candidate.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (candidate.phone && candidate.phone.includes(searchQuery)) ||
            roleName.toLowerCase().includes(searchQuery.toLowerCase())
        );
    });

    const allFilteredIds = filteredCandidates.map((c) => c._id);
    const isAllSelected = filteredCandidates.length > 0 && allFilteredIds.every((id) => selectedCandidateIds.includes(id));

    const handleSelectAll = () => {
        if (isAllSelected) {
            setSelectedCandidateIds((prev) => prev.filter((id) => !allFilteredIds.includes(id)));
        } else {
            setSelectedCandidateIds((prev) => [...new Set([...prev, ...allFilteredIds])]);
        }
    };

    const handleToggleCandidate = (e, id) => {
        e.stopPropagation();
        setSelectedCandidateIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    return (
        <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-0 sm:p-4 pb-20">
                <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #E2E8F0; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94A3B8; }
            `}</style>

                {/* Application Feed */}
                <div className="bg-white rounded-[40px] shadow-xl shadow-blue-900/5 border border-gray-50 flex flex-col h-[85vh] lg:h-[80vh]">
                    <div className="p-8 border-b border-gray-50 bg-[#F8FAFC]/50 rounded-t-[40px] space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <Clock className="w-4 h-4 text-blue-500 animate-pulse" />
                                    <span className="text-[10px] font-black uppercase text-blue-600 tracking-widest">Queue</span>
                                </div>
                                <h2 className="text-2xl font-black text-[#143E78]">Pending Review</h2>
                            </div>
                            {user?.role === 'admin' && filteredCandidates.length > 0 && (
                                <div className="flex items-center gap-2">
                                    <label className="flex items-center gap-1.5 cursor-pointer text-xs font-bold text-slate-600 hover:text-slate-800 bg-slate-100 px-2.5 py-1.5 rounded-xl border border-slate-200 select-none">
                                        <input
                                            type="checkbox"
                                            checked={isAllSelected}
                                            onChange={handleSelectAll}
                                            className="w-3.5 h-3.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                                        />
                                        <span>Select All</span>
                                    </label>
                                    {selectedCandidateIds.length > 0 && (
                                        <button
                                            onClick={() => setShowBulkDeleteModal(true)}
                                            className="flex items-center gap-1 bg-red-600 text-white px-3 py-1.5 rounded-xl text-xs font-bold hover:bg-red-700 transition-all shadow-sm animate-in fade-in duration-200"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                            Delete ({selectedCandidateIds.length})
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        {/* Search Bar */}
                        <div className="relative">
                            <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search by name, email, phone, role..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 text-xs border border-slate-200 rounded-xl outline-none focus:border-[#143E78] transition-all bg-white shadow-inner"
                            />
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
                        {filteredCandidates.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full opacity-30 text-center p-10">
                                <ShieldCheck className="w-16 h-16 mb-4 text-emerald-500 mx-auto" />
                                <p className="text-sm font-black uppercase tracking-widest">
                                    {searchQuery ? 'No Match Found' : 'Protocol Clear'}
                                </p>
                                <p className="text-[10px] font-bold uppercase mt-2">
                                    {searchQuery ? 'Try adjusting your search criteria' : 'All Applications Processed'}
                                </p>
                            </div>
                        ) : (
                            filteredCandidates.map(candidate => (
                                <div key={candidate._id} className="space-y-3">
                                    <button
                                        onClick={() => {
                                            if (selectedCandidate?._id === candidate._id) {
                                                setSelectedCandidate(null);
                                                setEditData({});
                                            } else {
                                                openCandidate(candidate);
                                            }
                                        }}
                                        className={`w-full text-left p-6 rounded-[32px] transition-all border outline-none group relative overflow-hidden ${selectedCandidate?._id === candidate._id
                                            ? 'bg-[#143E78] text-white border-blue-900 shadow-2xl -translate-x-1'
                                            : 'bg-gray-50 border-gray-100 text-gray-800 hover:bg-white hover:border-blue-200 hover:shadow-lg'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                {user?.role === 'admin' && (
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedCandidateIds.includes(candidate._id)}
                                                        onChange={(e) => handleToggleCandidate(e, candidate._id)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                                                    />
                                                )}
                                                <span className={`px-2.5 py-1 rounded-lg text-[8px] font-black uppercase tracking-widest ${selectedCandidate?._id === candidate._id ? 'bg-white/20 text-blue-100' : 'bg-blue-100 text-[#143E78]'}`}>
                                                    {candidate.role.replace(/_/g, ' ')}
                                                </span>
                                            </div>
                                            <span className={`text-[8px] font-bold px-2 py-0.5 rounded-full ${candidate.onboardingStatus === 'submitted' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-400'}`}>
                                                {candidate.onboardingStatus.toUpperCase()}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center text-[#143E78] font-black overflow-hidden">
                                                <CandidateAvatar
                                                    candidate={candidate}
                                                    textClasses="text-xs"
                                                />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <h4 className="font-black text-[15px] mb-0.5 truncate italic tracking-tight">{getDisplayName(candidate)}</h4>
                                                <p className="text-[10px] font-bold truncate opacity-60 tracking-wider uppercase">{candidate.email}</p>
                                            </div>
                                        </div>
                                    </button>
                                    {selectedCandidate?._id === candidate._id && (
                                        <div className="lg:hidden bg-white rounded-[32px] border border-gray-100 shadow-2xl overflow-hidden animate-in slide-in-from-top-4 duration-300">
                                            {renderDetailContent()}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Desktop Detail View */}
                <div className="hidden lg:block lg:col-span-2 bg-white rounded-[40px] shadow-2xl shadow-blue-900/10 border border-gray-50 overflow-hidden h-[85vh] lg:h-[80vh]">
                    {selectedCandidate ? renderDetailContent() : (
                        <div className="h-full flex flex-col items-center justify-center opacity-40 p-20 text-center">
                            <div className="w-32 h-32 bg-blue-50 rounded-[48px] flex items-center justify-center mb-8 rotate-6 shadow-inner">
                                <Activity className="w-14 h-14 text-[#143E78]" />
                            </div>
                            <h3 className="text-2xl font-black text-[#143E78] mb-2 uppercase tracking-tighter italic">Selection Required</h3>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.4em] max-w-sm mx-auto leading-relaxed">Initialize member profile review by selecting an active application from the institutional queue.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Iframe Modal for Fill Form on Behalf */}
            {iframeToken && (
                <div className="fixed inset-0 bg-slate-900/60 z-[100] flex items-center justify-center p-4 lg:p-8 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white w-full max-w-6xl h-[90vh] rounded-[40px] overflow-hidden shadow-2xl relative flex flex-col border border-gray-100">
                        <div className="flex justify-between items-center px-8 py-5 border-b border-gray-100 bg-slate-50 flex-shrink-0">
                            <h3 className="font-black text-[#143E78] uppercase tracking-[0.2em] text-[10px] lg:text-xs flex items-center gap-3">
                                <FileText className="w-5 h-5 text-indigo-500" />
                                Filling Form On Behalf of Member
                            </h3>
                            <button
                                onClick={() => {
                                    setIframeToken(null);
                                    setIframeRole(null);
                                    fetchCandidates(true);
                                }}
                                className="p-2.5 hover:bg-rose-100 text-rose-400 hover:text-rose-600 rounded-xl transition-all"
                                title="Close & Refresh"
                            >
                                <XCircle className="w-6 h-6" />
                            </button>
                        </div>
                        <iframe
                            src={`/onboarding-form/${iframeToken}?role=${iframeRole}`}
                            className="w-full flex-1 border-none"
                            title="Fill Form On Behalf"
                        />
                    </div>
                </div>
            )}
            {/* Bulk Delete Modal */}
            {showBulkDeleteModal && (
                <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 backdrop-blur-xs">
                    <div className="bg-white rounded-3xl p-6 sm:p-8 w-full max-w-md shadow-2xl space-y-4 animate-in zoom-in-95 duration-200 border border-gray-100">
                        <div className="flex items-center gap-3 text-red-600 font-black text-xl">
                            <div className="w-10 h-10 bg-red-100 rounded-2xl flex items-center justify-center shrink-0">
                                <Trash2 className="w-5 h-5 text-red-600" />
                            </div>
                            Confirm Bulk Delete
                        </div>
                        <p className="text-gray-600 text-sm leading-relaxed">
                            Are you sure you want to permanently delete <strong>{selectedCandidateIds.length} selected application(s)</strong>?
                            This action will completely wipe all application data, uploaded documents, and associated records from the database.
                        </p>
                        <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={() => setShowBulkDeleteModal(false)}
                                disabled={deletingBulk}
                                className="px-4 py-2 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition-all text-xs"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleBulkDeleteCandidates}
                                disabled={deletingBulk}
                                className="px-4 py-2 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-all text-xs flex items-center gap-2 shadow-lg shadow-red-600/20"
                            >
                                {deletingBulk ? (
                                    <>
                                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Deleting...
                                    </>
                                ) : (
                                    `Yes, Delete ${selectedCandidateIds.length} Applications`
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default OnboardingReview;
